import Module from '../lib/module';
import AccessibleAutocomplete from 'accessible-autocomplete';
import { DomEventApi } from '../lib/dom-events';

export default class Autocomplete extends Module {
  private autocompleteContainer: HTMLElement;
  private initialElementId: string;
  private isMultiple: boolean;
  private selectOptionElements: HTMLOptionElement[];
  private selectedOptionsList: HTMLElement;

  static FORM_GROUP_INPUT_CLASS = 'form-group__input';
  static FORM_GROUP_OPTIONS_CLASS = 'form-group__options';

  setup() {
    this.initialElementId = this.element.id;
    this.selectOptionElements = Array.prototype.slice.call(this.element.querySelectorAll('option'));
    this.isMultiple = (<HTMLElement> this.element as HTMLSelectElement).multiple;

    const container = document.createElement('div');
    container.classList.add('autocomplete');
    this.element.parentNode.insertBefore(container, this.element);
    this.autocompleteContainer = container;

    this.element.id = `${this.initialElementId}-select`;
    this.element.style.display = 'none';

    if (this.isMultiple) {
      this.selectedOptionsList = this.buildSelectedOptionsList();
      this.updateSelectedOptions();
    }

    this.bind();
  }

  destroy() {
    this.unbind();

    this.autocompleteContainer.remove();

    if (this.isMultiple) {
      this.selectedOptionsList.remove();
    }

    this.element.id = this.initialElementId;
    this.element.style.display = '';
  }

  private bind() {
    if (this.isMultiple) {
      AccessibleAutocomplete(Object.assign(this.defaultConfiguration, {
        source: (query, populateResults) => {
          const selectedValues = this.selectOptionElements.filter((option) => option.selected).map((option) => option.value);
          const matches = this.filterData(this.data, query, ((result) => !selectedValues.includes(result.value)));

          populateResults(matches);
        },
        placeholder: this.element.getAttribute('data-placeholder'),
      }));

      DomEventApi.on(this.element, 'change', event => {
        this.updateSelectedOptions();
      });

      DomEventApi.on(this.selectedOptionsList, 'change', 'input[type="checkbox"]', event => {
        event.stopPropagation();

        this.deselectOptionByValue(event.target.value);
      });
    } else {
      const selectedOption = this.selectOptionElements.find((option) => option.selected && option.value != '');
      const blankOption = this.selectOptionElements.find((option) => option.value == '');
      const placeholder = blankOption ? blankOption.innerText : null;

      AccessibleAutocomplete(Object.assign(this.defaultConfiguration, {
        dropdownArrow: (() => ''),
        defaultValue: selectedOption ? selectedOption.label : '',
        placeholder: placeholder,
      }));
    }

    this.autocompleteContainer.querySelector('input[type="text"]').classList.add(Autocomplete.FORM_GROUP_INPUT_CLASS);
  }

  private unbind() {
    if (this.isMultiple) {
      DomEventApi.off(this.element, 'change');
      DomEventApi.off(this.selectedOptionsList, 'change', 'input[type="checkbox"]');
    }
  }

  private clearInput() {
    const input: HTMLInputElement = this.autocompleteContainer.querySelector('input[type="text"]');
    const timeout = window.setTimeout(() => {
      input.value = '';
      input.dispatchEvent(new Event('input'));
    }, 1);
  }

  private filterData(data, query, condition?) {
    const lowercaseQuery = query.toLowerCase();

    return data.filter(result => {
      return result.label.toLowerCase().indexOf(lowercaseQuery) !== -1 && (condition === undefined || condition(result));
    });
  }

  private selectOptionByValue(value: string) {
    const option = this.selectOptionElements.find(option => option.value === value);
    option.selected = true;

    this.element.dispatchEvent(new Event('change'));
  }

  private deselectOptionByValue(value) {
    const option = this.selectOptionElements.find(option => option.value === value);
    option.selected = false;

    this.element.dispatchEvent(new Event('change'));
  }

  private updateSelectedOptions() {
    const optionsMarkup = this.selectOptionElements.filter(option => {
      return option.selected;
    }).map(option => {
      const itemId = `${this.initialElementId}-option-${option.value}`;

      return [
        `<li class="options-list__item">`,
          `<span class="options-list__control"><input type="checkbox" id="${itemId}" value="${option.value}" checked></span>`,
          `<label for="${itemId}">${option.innerText}</label>`,
        `</li>`
      ].join('');
    }).join("\n");

    if (optionsMarkup.length > 0) {
      this.selectedOptionsList.innerHTML = optionsMarkup;
      this.selectedOptionsList.style.display = '';
    } else if (this.selectedOptionsList.parentNode) {
      this.selectedOptionsList.innerHTML = '';
      this.selectedOptionsList.style.display = 'none';
    }
  }

  private buildSelectedOptionsList() {
    const list = document.createElement('ul');
    list.classList.add('options-list');
    list.classList.add(Autocomplete.FORM_GROUP_OPTIONS_CLASS);
    this.element.parentNode.parentNode.insertBefore(list, this.element.parentNode);

    return list;
  }

  private get data() {
    return this.selectOptionElements.filter(option => {
      // Ignore options without value or with blank value
      return (option.value || '').trim().length !== 0;
    }).map(option => {
      return {
        label: option.innerText,
        value: option.value,
      };
    });
  }

  private get defaultConfiguration() {
    return {
      id: this.initialElementId,
      name: '',
      element: this.autocompleteContainer,
      source: (query, populateResults) => {
        populateResults(this.filterData(this.data, query));
      },
      onConfirm: (result) => {
        if (!result) { return; }

        this.selectOptionByValue(result.value);

        if (this.isMultiple) {
          this.clearInput();
        }
      },
      showAllValues: !this.isMultiple,
      displayMenu: 'overlay',
      templates: {
        inputValue: function(item) {
          return item ? item.label : undefined;
        },
        suggestion: function(item) {
          return item.label;
        },
      },
      tNoResults: function() {
        return 'Keine Ergebnisse';
      },
      tAssistiveHint: function() {
        return 'Wenn Ergebnisse für die automatische Vervollständigung verfügbar sind, nutzen Sie die Aufwärts- und Abwärtspfeiltasten um Optionen durchzusehen und die Enter-Taste um auszuwählen. Auf Touch-Geräten können Sie durch Berühren oder mit Wischgesten navigieren.';
      },
      tStatusQueryTooShort: function(minQueryLength) {
        return `Tippen Sie ${minQueryLength} oder mehr Zeichen ein um Ergebnisse zu erhalten`;
      },
      tStatusNoResults: function() {
        return 'Keine Suchergebnisse';
      },
      tStatusSelectedOption: function(selectedOption, length, index) {
        return `${selectedOption} ${index + 1} von ${length} ist hervorgehoben`;
      },
      tStatusResults: function(length, contentSelectedOption) {
        const words = {
          result: (length === 1) ? 'Ergebnis' : 'Ergebnisse',
          is: (length === 1) ? 'ist' : 'sind',
        }

        return `${length} ${words.result} ${words.is} verfügbar. ${contentSelectedOption}`;
      },
    };
  };
}
